import cn from 'classnames';
import React, { FC, SyntheticEvent, memo, useMemo } from 'react';

import Decimal from 'decimal.js';

import Typography from '@mui/material/Typography';

import Button from '@components/common/Button';
import Tabs from '@components/common/Tabs';
import Tooltip from '@components/common/Tooltip';

import { Size, TabType, Variant } from '@constants/common/ui.constants';
import {
  ConnectorRangeWidthType,
  RANGE_TABs,
  RangeTab,
} from '@constants/vaults/vault.constants';

import { VaultFarmsType } from '@app-types/vaults/vault.types';

import RangeSelectionTooltip from './RangeSelectionTooltip';
import useStyles from './styles.module';

interface Props {
  selectedV3Farm: VaultFarmsType;
  isEditable: boolean;
  isCustomMode: boolean;
  quickButtons: {
    title: string;
    min: Decimal;
    max: Decimal;
    rangeWidthType: ConnectorRangeWidthType;
  }[];
  onChangeLmConfigMode: (
    _: SyntheticEvent,
    newValue: number | string,
  ) => Promise<void>;
  onClickQuickButton: (
    min: Decimal,
    max: Decimal,
    rangeType: ConnectorRangeWidthType | null,
  ) => void;
  onSetCustomMode: () => void;
  quickPresetType: ConnectorRangeWidthType | null;
}

const SelectRange: FC<Props> = ({
  selectedV3Farm,
  isEditable,
  onClickQuickButton,
  isCustomMode,
  quickButtons,
  onSetCustomMode,
  onChangeLmConfigMode,
  quickPresetType,
}) => {
  const { farm } = selectedV3Farm;

  const isDisabled = useMemo(
    () => !isEditable || selectedV3Farm.farm.isAilmEnabled,
    [isEditable, selectedV3Farm],
  );

  const activeModeTab = useMemo(
    () => (farm.isAilmEnabled ? RangeTab.Automatic : RangeTab.Manual),
    [farm],
  );

  const { classes } = useStyles({
    isAiLm: farm.isAilmEnabled,
    isDisabled: !isEditable,
  });

  const isActiveCustomMode = useMemo(
    () => !selectedV3Farm.farm.isAilmEnabled && quickPresetType === null,
    [selectedV3Farm, quickPresetType],
  );

  return (
    <div className={classes.box}>
      <div className={classes.inner}>
        <div className={classes.topPanel}>
          <div>
            <Typography variant="h3" component="div">
              Select Range
            </Typography>
            <Typography
              variant="body2"
              component="div"
              color={(theme) => theme.secondary.s3}
              className={classes.text}
            >
              Manual (ALM) or Automatic (AILM)
              <Tooltip title={<RangeSelectionTooltip />} isInfo />
            </Typography>
          </div>

          <Tabs
            isDark
            fullWidth
            activeTab={activeModeTab}
            options={RANGE_TABs}
            type={TabType.Internal}
            className={classes.tabs}
            isDisabled={!isEditable}
            onChange={onChangeLmConfigMode}
          />
        </div>

        <div className={classes.bottomPanel}>
          {quickButtons.map(({ title, min, max, rangeWidthType }) => {
            const isActive =
              !selectedV3Farm.farm.isAilmEnabled &&
              quickPresetType === rangeWidthType;

            return (
              <Button
                key={title}
                fullWidth
                size={Size.Small}
                variant={Variant.Range}
                disabled={isDisabled}
                className={cn(classes.btn, {
                  [classes.activeQuickBtn]: isActive && !isCustomMode,
                })}
                onClick={() => {
                  onClickQuickButton(min, max, rangeWidthType);
                }}
              >
                {title}
              </Button>
            );
          })}
          <Button
            fullWidth
            size={Size.Small}
            variant={Variant.Range}
            disabled={isDisabled}
            onClick={onSetCustomMode}
            className={cn(classes.btn, {
              [classes.activeQuickBtn]: isActiveCustomMode,
            })}
          >
            Custom
          </Button>
        </div>
      </div>
    </div>
  );
};

export default memo(SelectRange);
